import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ProductListEntry, StockItemView } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import { ExpiryBadge, storageEmoji, useToast } from './ui.js';

/** Espera o usuario parar de digitar antes de ir ao servidor. */
const DEBOUNCE_MS = 350;

interface Resultado {
  items: StockItemView[];
  /** Produtos que existem no catalogo mas nao estao em nenhuma dispensa. */
  foraDeEstoque: ProductListEntry[];
}

/**
 * "Onde esta o ovo?" — busca o item em todas as dispensas de uma vez.
 *
 * A pergunta que motiva a busca ("procurei na geladeira e nao achei") tem duas
 * respostas uteis, e a segunda costuma ser esquecida: ou o item esta em outra
 * dispensa, ou ele simplesmente acabou. Por isso, quando nada aparece no
 * estoque, a busca ainda olha o catalogo e diz "voce ja teve isso, mas nao tem
 * agora" — com o atalho para a lista de compras.
 */
export function StockSearch() {
  const { refresh } = useAppData();
  const toast = useToast();
  const [termo, setTermo] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);

  useEffect(() => {
    const consulta = termo.trim();
    if (consulta.length < 2) {
      setResultado(null);
      return;
    }

    let ativo = true;
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const items = await api.listItems({ q: consulta });
        if (!ativo) return;
        // So vasculha o catalogo quando o estoque nao respondeu — e a diferenca
        // entre "esta em outra dispensa" e "acabou".
        const foraDeEstoque =
          items.length === 0
            ? (await api.listProducts(consulta)).filter((produto) => produto.itemCount === 0)
            : [];
        if (ativo) setResultado({ items, foraDeEstoque });
      } catch (error) {
        if (ativo) toast((error as Error).message, 'error');
      } finally {
        if (ativo) setBuscando(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [termo, toast]);

  async function paraLista(produto: ProductListEntry) {
    try {
      await api.addShopping({ productId: produto.id, suggestedQuantity: 1, origin: 'manual' });
      toast(`${produto.name} entrou na lista de compras.`, 'ok');
      await refresh();
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }

  const total = resultado?.items.length ?? 0;

  return (
    <section className="card">
      <form onSubmit={(evento: FormEvent) => evento.preventDefault()}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="busca-estoque">🔎 Onde esta?</label>
          <input
            id="busca-estoque"
            value={termo}
            onChange={(evento) => setTermo(evento.target.value)}
            placeholder="Ex.: ovos, leite, sabao — procura em todas as dispensas"
            autoComplete="off"
          />
        </div>
      </form>

      {buscando ? <p className="small muted">Procurando…</p> : null}

      {!buscando && resultado && total > 0 ? (
        <>
          <p className="small muted">
            {total} {total === 1 ? 'item encontrado' : 'itens encontrados'}.
          </p>
          <ul className="list">
            {resultado.items.map((item) => (
              <li key={item.id} className="item">
                <div className="photo">{storageEmoji(item.storage.icon)}</div>
                <div className="info">
                  <div className="title">{item.product.name}</div>
                  <div className="sub">
                    <strong>{item.storage.name}</strong>
                    <ExpiryBadge item={item} />
                  </div>
                </div>
                <div className="qty">
                  {item.quantity} {item.product.unit}
                </div>
                <div className="actions">
                  <Link className="btn btn-sm" to={`/dispensa/${item.storage.id}`}>
                    Abrir
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {!buscando && resultado && total === 0 ? (
        <>
          <p className="small" style={{ color: 'var(--danger)' }}>
            Nao ha nada com esse nome em nenhuma dispensa.
          </p>
          {resultado.foraDeEstoque.length > 0 ? (
            <>
              <p className="small muted">Voce ja teve no catalogo — deve ter acabado:</p>
              <ul className="list">
                {resultado.foraDeEstoque.slice(0, 6).map((produto) => (
                  <li key={produto.id} className="item">
                    <div className="photo">🏷</div>
                    <div className="info">
                      <div className="title">{produto.name}</div>
                      <div className="sub">
                        <span className="badge">fora de estoque</span>
                        {produto.brand ? <span>{produto.brand}</span> : null}
                      </div>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => void paraLista(produto)}
                      >
                        🛒 Comprar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="small muted">
              Nem no catalogo. <Link to="/escanear">Escaneie</Link> ou{' '}
              <Link to="/sem-codigo">cadastre sem codigo</Link> se acabou de comprar.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}
